import { createI18n } from "vue-i18n";
import IntlMessageFormat from "intl-messageformat";
import en from "@/../i18n/en.json";
import { setDayjsLocale } from "@/helpers/dayjs.helper.js";

/** @typedef {'ar'|'cs'|'da'|'de'|'el'|'en'|'es'|'fr'|'hi'|'id'|'it'|'ja'|'ko'|'nl'|'no'|'pl'|'pt'|'ro'|'ru'|'sv'|'tr'|'uk'|'vi'|'zh'} SupportedLocale */
/** @typedef {Record<string, string | number | boolean | null | undefined>} TranslationParams */

/** @type {const} */
export const SUPPORTED_LOCALES = [
  "ar",
  "cs",
  "da",
  "de",
  "el",
  "en",
  "es",
  "fr",
  "hi",
  "id",
  "it",
  "ja",
  "ko",
  "nl",
  "no",
  "pl",
  "pt",
  "ro",
  "ru",
  "sv",
  "tr",
  "uk",
  "vi",
  "zh",
];

const loadedLocales = new Set(["en"]);
const localeLoadPromises = new Map();
const localeLoaders = {
  ar: () => import("@/../i18n/ar.json"),
  cs: () => import("@/../i18n/cs.json"),
  da: () => import("@/../i18n/da.json"),
  de: () => import("@/../i18n/de.json"),
  el: () => import("@/../i18n/el.json"),
  es: () => import("@/../i18n/es.json"),
  fr: () => import("@/../i18n/fr.json"),
  hi: () => import("@/../i18n/hi.json"),
  id: () => import("@/../i18n/id.json"),
  it: () => import("@/../i18n/it.json"),
  ja: () => import("@/../i18n/ja.json"),
  ko: () => import("@/../i18n/ko.json"),
  nl: () => import("@/../i18n/nl.json"),
  no: () => import("@/../i18n/no.json"),
  pl: () => import("@/../i18n/pl.json"),
  pt: () => import("@/../i18n/pt.json"),
  ro: () => import("@/../i18n/ro.json"),
  ru: () => import("@/../i18n/ru.json"),
  sv: () => import("@/../i18n/sv.json"),
  tr: () => import("@/../i18n/tr.json"),
  uk: () => import("@/../i18n/uk.json"),
  vi: () => import("@/../i18n/vi.json"),
  zh: () => import("@/../i18n/zh.json"),
};

/**
 * Compile ICU messages (e.g. `{count, plural, one {...} other {...}}`) for vue-i18n.
 * This keeps existing translation files compatible without rewriting message strings.
 */
const messageCompiler = (message, context) => {
  if (typeof message !== "string") {
    return () => context.key;
  }

  const formatter = new IntlMessageFormat(message, context.locale, undefined, { ignoreTag: true });
  return (messageContext) => formatter.format(messageContext.values);
};

/**
 * @param {string | undefined | null} language
 * @returns {SupportedLocale}
 */
export const normalizeLocale = (language) => {
  if (!language) {
    return "en";
  }
  const base = String(language).toLowerCase().replace("_", "-").split("-")[0];
  const aliases = {
    cz: "cs",
    nb: "no",
  };
  const normalized = aliases[base] || base;
  return SUPPORTED_LOCALES.includes(/** @type {SupportedLocale} */ (normalized))
    ? /** @type {SupportedLocale} */ (normalized)
    : "en";
};

/**
 * @param {string | undefined | null} language
 */
export const createI18nInstance = (language) =>
  createI18n({
    legacy: false,
    globalInjection: true,
    locale: normalizeLocale(language),
    fallbackLocale: "en",
    messages: { en },
    messageCompiler,
    missingWarn: false,
    fallbackWarn: false,
  });

const getComposer = (i18nOrComposer) =>
  i18nOrComposer?.global ? i18nOrComposer.global : i18nOrComposer;

export const loadLocaleMessages = async (i18nOrComposer, language) => {
  const composer = getComposer(i18nOrComposer);
  const locale = normalizeLocale(language);
  if (loadedLocales.has(locale)) {
    return locale;
  }

  const loader = localeLoaders[locale];
  if (!loader) {
    return "en";
  }

  if (!localeLoadPromises.has(locale)) {
    localeLoadPromises.set(
      locale,
      loader()
        .then((module) => {
          composer.setLocaleMessage(locale, module.default || module);
          loadedLocales.add(locale);
          return locale;
        })
        .catch((error) => {
          console.error(`Failed to load locale "${locale}"`, error);
          return "en";
        })
        .finally(() => {
          localeLoadPromises.delete(locale);
        }),
    );
  }

  return localeLoadPromises.get(locale);
};

export const setAppLocale = async (i18nOrComposer, language) => {
  const composer = getComposer(i18nOrComposer);
  const locale = await loadLocaleMessages(composer, language);
  await setDayjsLocale(locale);
  composer.locale.value = locale;
  return locale;
};

/**
 * @param {string} text
 * @returns {string}
 */
const formatMarkdown = (text = "") => {
  const escapedText = text.replace(/[<>]/g, (char) => ({ "<": "&lt;", ">": "&gt;" })[char]);
  const escapeAttr = (value) => String(value).replace(/&/g, "&amp;").replace(/"/g, "&quot;");
  const getSafeUrl = (rawUrl) => {
    try {
      const normalized = String(rawUrl || "").trim();
      const parsed = new URL(normalized, window.location.origin);
      const protocol = parsed.protocol.toLowerCase();
      if (["http:", "https:", "mailto:", "tel:"].includes(protocol)) {
        return parsed.href;
      }
      return null;
    } catch {
      return null;
    }
  };

  return escapedText
    .replace(/\*\*([^*]+?)\*\*/g, "<strong>$1</strong>")
    .replace(/\*([^*]+?)\*/g, "<em>$1</em>")
    .replace(/`([^`]+?)`/g, "<code>$1</code>")
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, (_, label, url) => {
      const safeUrl = getSafeUrl(url);
      if (!safeUrl) {
        return label;
      }
      return `<a href="${escapeAttr(safeUrl)}" target="_blank" rel="noopener noreferrer">${label}</a>`;
    })
    .replace(/(?:\\n|\r?\n)/g, "<br />");
};

/**
 * @param {import('vue-i18n').I18n} i18n
 */
export const createTranslationFormatPlugin = (i18n) => ({
  install: (app) => {
    app.config.globalProperties.$tf = (key, params = {}) => {
      const translated = i18n.global.t(key, params);
      return formatMarkdown(translated);
    };
  },
});
